import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, ArrowRight, Sparkles, Clock } from 'lucide-react';
import '../pages/HomePage.funnel.css';
import './WelcomePage.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const CAL_URL = 'https://calendly.com/thecollinsacademynew/30min';

const PLAN_META = {
  annual: {
    label: 'Pay In Full — $10,000 / year',
    sub: 'One-time payment · Save $2,000',
  },
  monthly: {
    label: 'Monthly Plan — $1,000 / month',
    sub: '12 months · $12,000 total',
  },
};

const WelcomePage = () => {
  const [params] = useSearchParams();
  const initialPlan = (params.get('plan') || 'annual').toLowerCase();
  const validPlan = ['annual', 'monthly'].includes(initialPlan) ? initialPlan : 'annual';

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    plan: validPlan,
    businessName: '',
    goal: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const planMeta = useMemo(() => PLAN_META[form.plan] || PLAN_META.annual, [form.plan]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/welcome/onboarding`, form);
      setSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong';
      setError(typeof msg === 'string' ? msg : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="tcf welcome-shell" data-testid="welcome-success">
        <section className="welcome-hero">
          <div className="welcome-hero-inner">
            <span className="welcome-badge">
              <Sparkles size={14} /> You're in.
            </span>
            <h1 className="welcome-title">
              Welcome to <span className="red">The Collins Academy.</span>
            </h1>
            <p className="welcome-lead">
              Your intake is locked in. The Collins team has been notified and will reach out
              within 24 hours to schedule your first CEO strategy call and kick off your
              done-for-you buildouts.
            </p>
            <div className="welcome-next">
              <div className="welcome-next-item">
                <CheckCircle2 size={22} />
                <div>
                  <div className="welcome-next-title">Check your inbox</div>
                  <div className="welcome-next-sub">
                    Confirmation email on the way with next-step instructions.
                  </div>
                </div>
              </div>
              <div className="welcome-next-item">
                <CheckCircle2 size={22} />
                <div>
                  <div className="welcome-next-title">Book your kickoff call</div>
                  <div className="welcome-next-sub">
                    Grab a slot now if you want to skip the queue and start faster.
                  </div>
                </div>
              </div>
              <div className="welcome-next-item">
                <CheckCircle2 size={22} />
                <div>
                  <div className="welcome-next-title">Watch for your monthly meet-up invite</div>
                  <div className="welcome-next-sub">
                    Details on the next in-person Collins Academy gathering will arrive by email.
                  </div>
                </div>
              </div>
            </div>
            <div className="welcome-cta-row">
              <a
                href={CAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="tcf-btn tcf-btn-primary tcf-btn-lg"
                data-testid="welcome-book-call"
              >
                <Clock size={18} /> Book Kickoff Call
              </a>
              <Link
                to="/"
                className="tcf-btn tcf-btn-secondary"
                data-testid="welcome-back-home"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="tcf welcome-shell" data-testid="welcome-page">
      <section className="welcome-hero">
        <div className="welcome-hero-inner welcome-hero-form">
          <div>
            <span className="welcome-badge">
              <CheckCircle2 size={14} /> Payment received
            </span>
            <h1 className="welcome-title">
              Welcome to <span className="red">The Collins Academy.</span>
            </h1>
            <p className="welcome-lead">
              You just made a big move. Complete this 30-second intake so the Collins team can
              open your file, schedule your first CEO strategy call, and kick off the done-for-you
              buildouts.
            </p>
            <div className="welcome-plan-card" data-testid="welcome-plan-card">
              <span className="welcome-plan-tag">Your plan</span>
              <div className="welcome-plan-name">{planMeta.label}</div>
              <div className="welcome-plan-sub">{planMeta.sub}</div>
            </div>
          </div>

          <form className="welcome-form" onSubmit={submit} data-testid="welcome-form">
            <div className="welcome-row">
              <label>
                <span>First name</span>
                <input
                  type="text"
                  required
                  value={form.firstName}
                  onChange={onChange('firstName')}
                  data-testid="welcome-first-name"
                />
              </label>
              <label>
                <span>Last name</span>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={onChange('lastName')}
                  data-testid="welcome-last-name"
                />
              </label>
            </div>
            <label>
              <span>Email</span>
              <input
                type="email"
                required
                value={form.email}
                onChange={onChange('email')}
                data-testid="welcome-email"
              />
            </label>
            <label>
              <span>Mobile phone</span>
              <input
                type="tel"
                required
                value={form.phone}
                onChange={onChange('phone')}
                placeholder="(555) 555-5555"
                data-testid="welcome-phone"
              />
            </label>
            <label>
              <span>Business or personal brand (optional)</span>
              <input
                type="text"
                value={form.businessName}
                onChange={onChange('businessName')}
                data-testid="welcome-business"
              />
            </label>
            <label>
              <span>What's your #1 goal for the next 90 days? (optional)</span>
              <textarea
                rows={3}
                value={form.goal}
                onChange={onChange('goal')}
                placeholder="E.g. Get funding-ready and place my first $50K credit line."
                data-testid="welcome-goal"
              />
            </label>

            <div className="welcome-plan-toggle">
              <span className="welcome-plan-toggle-label">Confirm plan</span>
              <div className="welcome-plan-toggle-options">
                <button
                  type="button"
                  className={`welcome-plan-opt ${form.plan === 'annual' ? 'active' : ''}`}
                  onClick={() => setForm((f) => ({ ...f, plan: 'annual' }))}
                  data-testid="welcome-plan-annual"
                >
                  Pay In Full · $10K
                </button>
                <button
                  type="button"
                  className={`welcome-plan-opt ${form.plan === 'monthly' ? 'active' : ''}`}
                  onClick={() => setForm((f) => ({ ...f, plan: 'monthly' }))}
                  data-testid="welcome-plan-monthly"
                >
                  Monthly · $1K/mo
                </button>
              </div>
            </div>

            {error && (
              <div className="welcome-error" data-testid="welcome-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="tcf-btn tcf-btn-primary tcf-btn-lg welcome-submit"
              disabled={submitting}
              data-testid="welcome-submit"
            >
              {submitting ? 'Sending…' : 'Complete Intake'} <ArrowRight size={18} />
            </button>
            <p className="welcome-fineprint">
              Your details are only used to onboard you into The Collins Academy. We never share
              or sell information.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default WelcomePage;
